/**
 * DemographicsPanel.jsx — v-bonat-dash 2026-06
 *
 * لوحة التوزيع الديموغرافي للعملاء المطابَقين في رتم (نفس مصدر build_audience).
 * كل الأرقام والنِسب تأتي محسوبة من الباك-إند — لا حساب في المتصفح.
 * تظهر لمن يملك customers.bonat_upload فقط.
 */
import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { formatKSA } from "../lib/ksaTime";

const arNum = (n) => (n || 0).toLocaleString("ar");

function Bar({ label, count, pct, hi }) {
  return (
    <div data-testid={`demo-row-${label}`} style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13,
                    color: hi ? "#B45309" : "#3D2A1C", fontWeight: hi ? 800 : 600 }}>
        <span>{label}</span>
        <span>{arNum(count)} · {pct}%</span>
      </div>
      <div style={{ height: 8, background: "#EDE9FE", borderRadius: 999, overflow: "hidden" }}>
        <div style={{ width: `${Math.min(pct, 100)}%`, height: "100%",
                      background: hi ? "#F59E0B" : "#7C3AED" }} />
      </div>
    </div>
  );
}

export default function DemographicsPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("all");

  const load = () => {
    api.get("/customers/bonat/distribution")
      .then((r) => { setData(r.data); setErr(null); })
      .catch((e) => setErr(e?.response?.status === 403 ? null : "تعذّر تحميل التوزيع"));
  };
  useEffect(() => { load(); }, []);

  if (err) return (
    <div data-testid="demographics-error" style={{ color: "#B91C1C", fontSize: 13 }}>{err}</div>
  );
  if (!data) return null;

  const ageSource = tab === "all" ? data.age : (data.cross?.[tab] || data.age);

  return (
    <section data-testid="demographics-panel" style={{
      marginBottom: 24, padding: 20, borderRadius: 16, background: "#FFFFFF",
      border: "2px solid rgba(124,58,237,0.25)",
    }}>
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#5B21B6" }}>
        📊 التوزيع الديموغرافي
      </h2>

      {/* التغطية الصريحة: إجمالي رتم مقابل المُثرَى */}
      <div data-testid="demographics-coverage" style={{
        marginTop: 10, padding: 10, borderRadius: 10, background: "#F5F3FF",
        fontSize: 13, color: "#5B21B6", fontWeight: 700 }}>
        إجمالي عملاء رتم: <b>{arNum(data.coverage?.retm_total)}</b>
        {" · "}منهم مُثرَى ديموغرافيًا: <b>{arNum(data.coverage?.enriched_matched)}</b>
        {" "}(<b>{data.coverage?.coverage_pct}%</b> تغطية)
      </div>
      <div style={{ fontSize: 11, color: "#78716C", marginTop: 6 }}>
        المصدر: عملاء رتم المطابَقون فقط (نفس مصدر بناء الجمهور) · آخر رفع بونات:{" "}
        {data.as_of_last_bonat_upload ? formatKSA(new Date(data.as_of_last_bonat_upload)) : "—"}
      </div>

      {/* الجنس */}
      <h3 style={{ fontSize: 14, fontWeight: 800, color: "#5B21B6", margin: "16px 0 8px" }}>الجنس</h3>
      {(data.gender || []).map((g) => (
        <Bar key={g.key} label={g.label} count={g.count} pct={g.pct} hi={g.key === "unknown"} />
      ))}

      {/* الأعمار + تبويب التقاطع */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                    margin: "16px 0 8px" }}>
        <h3 style={{ fontSize: 14, fontWeight: 800, color: "#5B21B6", margin: 0 }}>
          الأعمار {tab !== "all" ? `(${tab === "male" ? "رجال" : tab === "female" ? "نساء" : "غير محدد"})` : ""}
        </h3>
        <div style={{ display: "flex", gap: 6 }}>
          {[["all", "الكل"], ["male", "رجال"], ["female", "نساء"], ["unknown", "غير محدد"]].map(([k, l]) => (
            <button key={k} type="button" data-testid={`demo-tab-${k}`} onClick={() => setTab(k)}
                    style={{ padding: "4px 10px", borderRadius: 8, fontSize: 12, fontWeight: 700,
                             border: "1px solid", borderColor: tab === k ? "#7C3AED" : "#e5e7eb",
                             background: tab === k ? "#EDE9FE" : "white",
                             color: tab === k ? "#5B21B6" : "#475569" }}>
              {l}
            </button>
          ))}
        </div>
      </div>
      {(ageSource || []).map((a) => (
        <Bar key={a.key} label={a.label} count={a.count} pct={a.pct}
             hi={a.key === "under_18" || a.key === "unknown"} />
      ))}

      {/* ملاحظة القاصرين */}
      <div data-testid="demographics-minors-note" style={{
        marginTop: 12, padding: 10, borderRadius: 8, background: "#EDE9FE",
        color: "#5B21B6", fontSize: 12, fontWeight: 700 }}>
        👶 دون 18: {arNum(data.minors?.total)} ({data.minors?.pct}%) —{" "}
        {data.minors?.note}
      </div>
    </section>
  );
}
